import { object, number, string } from 'yup'

import { createProject } from '@pepper/utils/src/api/projects/projects'
import { attachDomainWithProject } from '@pepper/utils/src/api/seo'

export type FormValues = {
  projectId: string
  verticalId: number
  projectName?: string
  domainId?: string
}

export const initialValues: FormValues = {
  domainId: '',
} as FormValues

export const validationSchema = object().shape(
  {
    verticalId: number().typeError('Please select content type.'),
    projectId: string()
      .trim()
      .when('projectName', {
        is: val => val && val.length > 0,
        then: string().notRequired().nullable(),
        otherwise: string().required('Please select a project.'),
      }),
    projectName: string()
      .trim()
      .when('projectId', {
        is: val => val && val === 'new',
        then: string().required('Please enter project name.'),
      }),
    domainId: string().when('projectId', {
      is: val => val && val === 'new',
      then: string().required('Please select a domain.'),
    }),
  },
  [['projectId', 'projectName']]
)

type Args = {
  values: FormValues
  projects: Project[]
  items: Partial<{
    assignment_title: string
    content_gap_report_id: string
    word_count: number | string
  }>[]
  verticals: VerticalObjectType[]
  companyId: string
}

export const generateDraftData = async ({
  values,
  projects,
  items,
  verticals,
  companyId,
}: Args) => {
  let draftData
  let projectId

  const { verticalId, projectName } = values
  const selectedVertical = verticals.find(item => item.id === verticalId)
  if (values.projectId && values.projectId !== 'new') {
    draftData = {
      project_id: values.projectId,
      vertical_id: verticalId,
      project_title: projects.find(project => project.id === values.projectId)
        ?.project_title,
      assignments: items,
    }
    projectId = values.projectId
  } else {
    const {
      status: _status,
      error: _error,
      data,
    } = await createProject({
      body: {
        assignments: [],
        company_id: companyId,
        project_title: projectName,
      },
    })
    if (_status !== 'ok' || data === null) {
      return { error: _error }
    }
    const attachDomainResponse = await attachDomainWithProject({
      companyId,
      projectId: data.project_id,
      domainId: values.domainId!,
    })
    if (attachDomainResponse.status !== 'ok') {
      return { error: attachDomainResponse.error }
    }
    draftData = {
      company_id: companyId,
      project_category: selectedVertical?.category.id,
      vertical_id: verticalId,
      project_id: data.project_id,
      project_title: projectName,
      assignments: items,
    }
    projectId = data.project_id
  }
  return { draftData, projectId }
}
